import { SlackNotificationType } from "@/lib/shared/enums";
import * as Sentry from "@sentry/nextjs";

/**
 * Hard cap on each Slack webhook POST. Notifications are best-effort and usually run inside a
 * deferred `after()` callback; without a timeout a stalled webhook would pin the instance open.
 */
export const SLACK_SEND_TIMEOUT_MS = 5000;

/**
 * Hard cap on the dedupe lookup. Dedupe is an optimisation, so a slow endpoint must never hold up
 * a notification: on timeout the message is posted anyway.
 */
export const SLACK_DEDUPE_TIMEOUT_MS = 500;

/** Why a send did not succeed. Absent on success. */
export type SlackSendFailureReason = "missing_env" | "http_error" | "timeout" | "network_error";

/** Payload accepted by {@link sendSlack}. At least one of `text`, `blocks`, `attachments` should be set. */
export interface SlackSendParams {
  /** Which `SLACK_WEBHOOK_URL_<channel>` env var to post to. */
  channel: SlackNotificationType;
  /** Top-level message text (also the notification preview; `@mentions` only ping from here). */
  text?: string;
  /** Top-level Block Kit blocks. */
  blocks?: Record<string, unknown>[];
  /** Legacy colored-sidebar attachments. */
  attachments?: Record<string, unknown>[];
  /**
   * Duplicate-suppression key (see `idempotency-keys.ts`). When `SLACK_DEDUPE_URL` is set, the
   * backend is asked whether the key was already used and a duplicate is not posted. Fails open:
   * if dedupe is unavailable the message is posted.
   */
  idempotencyKey?: string;
}

/** Outcome of a send. `reason` explains a failure for wrappers that need to tell failure kinds apart. */
export interface SlackSendResult {
  ok: boolean;
  reason?: SlackSendFailureReason;
  /** True when the send was skipped because the idempotency key was already used. */
  deduped?: true;
}

/** Channels whose missing-env condition was already logged, so the log fires once per channel. */
const loggedMissingEnv = new Set<string>();

/**
 * Clears the log-once state. Exists so tests can exercise the first-failure log repeatedly.
 *
 * @returns Nothing.
 */
export function resetSlackSenderStateForTests(): void {
  loggedMissingEnv.clear();
}

/** Whether a thrown fetch error is a timeout/abort rather than a network failure. */
function isTimeoutOrAbort(error: unknown): boolean {
  return error instanceof Error && (error.name === "TimeoutError" || error.name === "AbortError");
}

/**
 * Reports a failed send to Sentry and the logs. Carries only the channel, status and reason: never
 * the message body, the URL or any payload value.
 */
function reportFailure(channel: string, reason: SlackSendFailureReason, status?: number): void {
  console.error("slack-sender: send failed", { channel, reason, status });
  try {
    Sentry.captureMessage("Slack send failed", {
      level: "error",
      tags: { channel, reason, status: status === undefined ? "none" : String(status) },
    });
  } catch {
    // Reporting must never throw into the caller.
  }
}

/**
 * Posts a payload to a concrete webhook URL with the shared timeout and failure handling. NEVER
 * throws.
 *
 * @param webhookUrl - Destination incoming webhook URL.
 * @param channel - Label used for logs and Sentry tags (a channel name, never a URL).
 * @param payload - JSON body to POST.
 * @returns `{ ok: true }` on a 2xx, otherwise `{ ok: false, reason }`.
 */
export async function postToSlackUrl(
  webhookUrl: string,
  channel: string,
  payload: Record<string, unknown>
): Promise<SlackSendResult> {
  try {
    const response = await fetch(webhookUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(SLACK_SEND_TIMEOUT_MS),
    });
    if (!response.ok) {
      reportFailure(channel, "http_error", response.status);
      return { ok: false, reason: "http_error" };
    }
    return { ok: true };
  } catch (error) {
    const reason: SlackSendFailureReason = isTimeoutOrAbort(error) ? "timeout" : "network_error";
    reportFailure(channel, reason);
    return { ok: false, reason };
  }
}

/**
 * Asks the backend dedupe endpoint whether this key was already used. Fails open: an unset URL or
 * API key, a timeout, a network error, a non-2xx or a malformed body all answer `false`. NEVER
 * throws and never logs the key.
 *
 * @param channel - Label for logs (a channel name, never a URL).
 * @param idempotencyKey - The caller's duplicate-suppression key.
 * @returns `true` only when the backend explicitly reports a duplicate.
 */
async function isDuplicateSend(channel: string, idempotencyKey: string): Promise<boolean> {
  const dedupeUrl = process.env.SLACK_DEDUPE_URL;
  const apiKey = process.env.NOWADAYS_API_KEY;
  if (!dedupeUrl || !apiKey) return false;

  try {
    const response = await fetch(dedupeUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-API-Token": apiKey },
      body: JSON.stringify({ key: idempotencyKey }),
      signal: AbortSignal.timeout(SLACK_DEDUPE_TIMEOUT_MS),
    });
    if (!response.ok) {
      console.warn("slack-sender: dedupe unavailable, posting anyway", { channel, status: response.status });
      return false;
    }
    const body: unknown = await response.json();
    if (typeof body !== "object" || body === null) return false;
    return (body as { duplicate?: unknown }).duplicate === true;
  } catch (error) {
    const reason = isTimeoutOrAbort(error) ? "timeout" : "network_error";
    console.warn("slack-sender: dedupe unavailable, posting anyway", { channel, reason });
    return false;
  }
}

/**
 * The single server-only Slack sender. Posts `text` / `blocks` / `attachments` to the
 * `SLACK_WEBHOOK_URL_<channel>` webhook with a 5s timeout, checks the response status, reports
 * failures to Sentry (channel and status only) and NEVER throws, so it is safe in `after()`.
 * A missing env var is logged once per channel and returns `{ ok: false }`. With an
 * `idempotencyKey` and `SLACK_DEDUPE_URL` set, a duplicate is skipped and returns
 * `{ ok: true, deduped: true }`; dedupe fails open.
 *
 * @param params - See {@link SlackSendParams}.
 * @returns `{ ok }` plus a failure `reason` or `deduped`; never rejects.
 */
export async function sendSlack(params: SlackSendParams): Promise<SlackSendResult> {
  const { channel, text, blocks, attachments, idempotencyKey } = params;
  const webhookUrl = process.env[`SLACK_WEBHOOK_URL_${channel}`];
  if (!webhookUrl) {
    if (!loggedMissingEnv.has(channel)) {
      loggedMissingEnv.add(channel);
      console.error(`SLACK_WEBHOOK_URL_${channel} is not defined; skipping Slack notification.`);
    }
    return { ok: false, reason: "missing_env" };
  }

  if (idempotencyKey && (await isDuplicateSend(channel, idempotencyKey))) {
    return { ok: true, deduped: true };
  }

  const payload: Record<string, unknown> = {};
  if (text !== undefined) payload.text = text;
  if (blocks) payload.blocks = blocks;
  if (attachments) payload.attachments = attachments;
  return postToSlackUrl(webhookUrl, channel, payload);
}
